/* eslint-disable react/forbid-prop-types */
import React from 'react';
import PropTypes from 'prop-types';
import { Modal } from 'react-bootstrap';
import { FiUser, FiMapPin, FiMail, FiPhone } from 'react-icons/fi';
import { formatPrice, formatEntry } from '../../utils/onlineVisaUtils';
import { VISA_PAX_TYPES } from '../../constants/onlineVisaConstants';
import './BookingReviewModal.scss';

const TYPE_LABEL = {
  [VISA_PAX_TYPES.ADULT]: 'Adult',
  [VISA_PAX_TYPES.CHILD]: 'Child',
  [VISA_PAX_TYPES.INFANT]: 'Infant',
};

function BookingReviewModal({
  show,
  onHide,
  onConfirm,
  visa,
  travelers,
  contactInfo,
  submitting,
  totalCost,
  currency,
}) {
  const cur = currency || visa?.visaFare?.currency || 'INR';
  return (
    <Modal
      show={show}
      onHide={submitting ? () => {} : onHide}
      centered
      backdrop={submitting ? 'static' : true}
      dialogClassName="booking-review-modal"
    >
      <Modal.Header closeButton={!submitting}>
        <Modal.Title className="booking-review-modal__title">
          Review &amp; Confirm Booking
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <section className="booking-review-modal__section">
          <div className="booking-review-modal__section-head">
            <FiMapPin />
            <span>Visa</span>
          </div>
          <div className="booking-review-modal__visa">
            <div className="booking-review-modal__visa-top">
              <strong>
                {visa?.destinationName ||
                  visa?.destination.destinationName ||
                  'Visa'}
              </strong>
              <span className="booking-review-modal__visa-type">
                {(visa?.visaType || '').toUpperCase()}
              </span>
            </div>
            <div className="booking-review-modal__visa-meta">
              <span>
                {formatEntry(visa?.entry)}
                {visa?.durationDisplay ? ` · ${visa.durationDisplay}` : ''}
              </span>
              {visa?.minTravelDays != null && (
                <span>· Processing {visa.minTravelDays} days</span>
              )}
            </div>
          </div>
        </section>

        <section className="booking-review-modal__section">
          <div className="booking-review-modal__section-head">
            <FiUser />
            <span>Travelers ({travelers.length})</span>
          </div>
          <ul className="booking-review-modal__pax-list">
            {travelers.map((p) => (
              <li
                key={`${p.type}-${p.seatIndex}`}
                className="booking-review-modal__pax"
              >
                <div className="booking-review-modal__pax-main">
                  <span className="booking-review-modal__pax-name">
                    {[p.title, p.firstName, p.lastName]
                      .filter(Boolean)
                      .join(' ') || '—'}
                  </span>
                  <span className="booking-review-modal__pax-type">
                    {TYPE_LABEL[p.type] || p.type}
                    {p.primary ? ' · Lead' : ''}
                  </span>
                </div>
                <div className="booking-review-modal__pax-meta">
                  <span>DOB: {p.dateOfBirth || '—'}</span>
                  <span>
                    Age: {p.age === '' || p.age == null ? '—' : p.age}
                  </span>
                  <span>Passport: {p.passportNumber || '—'}</span>
                </div>
              </li>
            ))}
          </ul>
        </section>

        <section className="booking-review-modal__section">
          <div className="booking-review-modal__section-head">
            <FiMail />
            <span>Contact</span>
          </div>
          <div className="booking-review-modal__contact">
            <span>
              <FiMail />
              {contactInfo?.email || '—'}
            </span>
            <span>
              <FiPhone />
              {contactInfo?.countryCode || ''} {contactInfo?.phone || '—'}
            </span>
          </div>
        </section>

        <div className="booking-review-modal__total">
          <span>Total Amount</span>
          <strong>
            {formatPrice(totalCost, cur) || `${cur} ${totalCost}`}
          </strong>
        </div>
      </Modal.Body>
      <Modal.Footer className="booking-review-modal__footer">
        <button
          type="button"
          className="booking-review-modal__cancel"
          onClick={onHide}
          disabled={submitting}
        >
          Go Back
        </button>
        <button
          type="button"
          className="booking-review-modal__confirm"
          onClick={onConfirm}
          disabled={submitting}
        >
          {submitting ? 'Booking…' : 'Confirm & Book'}
        </button>
      </Modal.Footer>
    </Modal>
  );
}

BookingReviewModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  visa: PropTypes.object,
  travelers: PropTypes.array.isRequired,
  contactInfo: PropTypes.object,
  submitting: PropTypes.bool,
  totalCost: PropTypes.number,
  currency: PropTypes.string,
};

BookingReviewModal.defaultProps = {
  visa: null,
  contactInfo: null,
  submitting: false,
  totalCost: 0,
  currency: '',
};

export default BookingReviewModal;
